import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserAccess } from '../hooks/useUserAccess';
import { useNavigate } from 'react-router-dom';

export const UpgradePage = () => {
  const { currentUser } = useAuth();
  const { userAccess } = useUserAccess();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [userCurrency, setUserCurrency] = useState({ code: 'NZD', symbol: '$' });

  // Detect user's location and set currency
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const country = data.country_code;
        
        const currencyMap = {
          'US': { code: 'USD', symbol: '$', upgradePrice: 21, originalUpgradePrice: 28 },
          'AU': { code: 'AUD', symbol: '$', upgradePrice: 32.25, originalUpgradePrice: 43 },
          'GB': { code: 'GBP', symbol: '£', upgradePrice: 16.50, originalUpgradePrice: 22 },
          'NZ': { code: 'NZD', symbol: '$', upgradePrice: 37.50, originalUpgradePrice: 50 },
        };
        
        setUserCurrency(currencyMap[country] || currencyMap['NZ']);
      } catch (error) {
        console.error('Failed to detect location:', error);
        setUserCurrency({ code: 'NZD', symbol: '$', upgradePrice: 37.50, originalUpgradePrice: 50 });
      }
    };
    
    detectCurrency();
  }, []);

  useEffect(() => {
    // If user doesn't have access or already has collaborative product, redirect
    if (!userAccess) return;
    
    if (!userAccess.hasAccess) {
      // No access at all - redirect to main purchase
      navigate('/purchase');
    } else if (userAccess.isCollaborativeProduct) {
      // Already has collaborative - redirect to create story
      navigate('/story/new');
    }
  }, [userAccess, navigate]);

  const handleUpgrade = async () => {
    if (!currentUser || !userAccess) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      // Get current user's ID token
      const idToken = await currentUser.getIdToken();
      
      // Call Firebase function to create Stripe checkout session
      const response = await fetch('https://us-central1-memory-stream-12e9d.cloudfunctions.net/createStripeCheckout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          productId: 'memory-stream-for-two',
          customerEmail: currentUser.email,
          customerName: currentUser.displayName || currentUser.email,
          isUpgrade: true,
          upgradeFromOrderId: userAccess.orderId,
          successUrl: `${window.location.origin}/upgrade-success`,
          cancelUrl: `${window.location.origin}/upgrade`
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkoutUrl;
      
    } catch (err) {
      console.error('Error initiating upgrade:', err);
      setError('Failed to start checkout process. Please try again.');
      setProcessing(false);
    }
  };

  if (!userAccess || !userAccess.hasAccess || userAccess.isCollaborativeProduct) {
    return (
      <div className="max-w-2xl mx-auto mt-16">
        <div className="memory-stream-card text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-8">
      <div className="memory-stream-card">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="ms-heading text-3xl mb-2" style={{ color: 'var(--ms-primary)' }}>
            Upgrade to Memory Stream For Two
          </h1>
          <p className="text-lg text-gray-600">
            Create collaborative stories with a second author
          </p>
        </div>

        {/* Current Status */}
        <div className="rounded-lg p-6 mb-8" style={{ backgroundColor: '#f8f5f0', border: '1px solid var(--ms-secondary)' }}>
          <h3 className="font-semibold mb-3" style={{ color: 'var(--ms-primary)' }}>Your Current Plan</h3>
          <div style={{ color: 'var(--ms-primary)' }}>
            <div className="flex justify-between">
              <span>Product:</span>
              <span className="font-medium">Memory Stream For One</span>
            </div>
          </div>
        </div>

        {/* Upgrade Offer */}
        <div className="rounded-lg p-8 mb-8" style={{ backgroundColor: '#f8f5f0', border: '2px solid var(--ms-secondary)' }}>
          <div className="text-center mb-6">
            <h2 className="ms-heading text-2xl mb-2" style={{ color: 'var(--ms-primary)' }}>
              Upgrade Offer
            </h2>
            <div className="text-4xl font-bold mb-2" style={{ color: 'var(--ms-secondary)' }}>
              {userCurrency.symbol}{userCurrency.upgradePrice} {userCurrency.code}
            </div>
            <div className="text-sm" style={{ color: 'var(--ms-secondary)' }}>
              25% off (Regular: {userCurrency.symbol}{userCurrency.originalUpgradePrice})
            </div>
            <p className="mt-2" style={{ color: 'var(--ms-primary)' }}>
              Upgrade to unlock collaborative storytelling
            </p>
          </div>

          <div className="bg-white rounded-lg p-6 mb-6">
            <h3 className="font-semibold mb-4" style={{ color: 'var(--ms-primary)' }}>What You'll Get:</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="mr-2 text-xl" style={{ color: 'var(--ms-accent-green)' }}>•</span>
                <div>
                  <div className="font-medium" style={{ color: 'var(--ms-primary)' }}>Two-Author Collaboration</div>
                  <div className="text-sm text-gray-600">Both authors can answer questions and contribute</div>
                </div>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-xl" style={{ color: 'var(--ms-accent-green)' }}>•</span>
                <div>
                  <div className="font-medium" style={{ color: 'var(--ms-primary)' }}>Shared Story Creation</div>
                  <div className="text-sm text-gray-600">Work together on the same story in real-time</div>
                </div>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-xl" style={{ color: 'var(--ms-accent-green)' }}>•</span>
                <div>
                  <div className="font-medium" style={{ color: 'var(--ms-primary)' }}>Multiple Perspectives</div>
                  <div className="text-sm text-gray-600">Capture different viewpoints of shared experiences</div>
                </div>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-xl" style={{ color: 'var(--ms-accent-green)' }}>•</span>
                <div>
                  <div className="font-medium" style={{ color: 'var(--ms-primary)' }}>All Original Features</div>
                  <div className="text-sm text-gray-600">Keep everything from Memory Stream For One</div>
                </div>
              </li>
            </ul>
          </div>

          {/* Important Warning */}
          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: '#fff8f0', borderLeft: '4px solid var(--ms-secondary)' }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5" style={{ color: 'var(--ms-secondary)' }} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium" style={{ color: 'var(--ms-primary)' }}>
                  Important: Collaborative stories must be created from scratch
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Once questions have been generated for a story, you cannot add a second author. The upgrade only applies to new collaborative stories you create after upgrading.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#fff0f0', border: '1px solid var(--ms-accent-red)', color: 'var(--ms-accent-red)' }}>
              {error}
            </div>
          )}
          
          <button
            onClick={handleUpgrade}
            disabled={processing}
            className="memory-stream-button w-full px-8 py-4 text-lg font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Processing...
              </span>
            ) : (
              'Upgrade Now for $59 NZD'
            )}
          </button>
        </div>

        {/* FAQ / Details */}
        <div className="pt-6" style={{ borderTop: '1px solid #e5e5e5' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--ms-primary)' }}>Perfect For:</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div className="flex items-start">
              <span className="mr-2" style={{ color: 'var(--ms-secondary)' }}>•</span>
              <span>Couples creating their shared journey</span>
            </div>
            <div className="flex items-start">
              <span className="mr-2" style={{ color: 'var(--ms-secondary)' }}>•</span>
              <span>Parents and children collaborating</span>
            </div>
            <div className="flex items-start">
              <span className="mr-2" style={{ color: 'var(--ms-secondary)' }}>•</span>
              <span>Best friends documenting memories</span>
            </div>
            <div className="flex items-start">
              <span className="mr-2" style={{ color: 'var(--ms-secondary)' }}>•</span>
              <span>Siblings sharing family history</span>
            </div>
          </div>
        </div>

        {/* Cancel */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/story/new')}
            className="underline transition-colors"
            style={{ color: 'var(--ms-primary)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--ms-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--ms-primary)'}
          >
            Maybe later, go back to story creation
          </button>
        </div>
      </div>
    </div>
  );
};
